var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * CZNA-312 — reads the vocabulary-type registry from
 * GET /czna-authorities/vocabulary-types.
 *
 * <p>This is the de-enumeration path: instead of a hardcoded `RecordType`
 * enum + exhaustive maps, the admin GUI fetches the type catalog (code,
 * Czech/English labels, validationType, bindings) from the backend. The
 * CLOSED controlled vocabularies (formerly hardcoded as `VOCABULARY_*` enum
 * members) are surfaced through this hook.
 *
 * <p>`validationType` narrows the result to OPEN or CLOSED types; omit it to
 * fetch all. The query is keyed by `validationType` so switching the filter
 * refetches without the unstable `ky` reference retriggering the query.
 */
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { fetchVocabularyTypes, createVocabularyType, updateVocabularyType } from '../api/vocabularyService';
var VOCABULARY_TYPES_KEY = 'czna-vocabulary-types';
export var useVocabularyTypes = function (opts) {
    var _a;
    if (opts === void 0) { opts = {}; }
    var ky = useOkapiKy();
    var validationType = opts.validationType, includeBindings = opts.includeBindings, includeTermCounts = opts.includeTermCounts, _b = opts.enabled, enabled = _b === void 0 ? true : _b;
    var _c = useQuery([VOCABULARY_TYPES_KEY, validationType !== null && validationType !== void 0 ? validationType : null, includeBindings !== null && includeBindings !== void 0 ? includeBindings : false, includeTermCounts !== null && includeTermCounts !== void 0 ? includeTermCounts : false], function () { return fetchVocabularyTypes(ky, { validationType: validationType, includeBindings: includeBindings, includeTermCounts: includeTermCounts }); }, { enabled: enabled }), data = _c.data, isLoading = _c.isLoading, isError = _c.isError;
    return {
        vocabularyTypes: (_a = data === null || data === void 0 ? void 0 : data.vocabularyTypes) !== null && _a !== void 0 ? _a : [],
        isLoading: isLoading,
        isError: isError,
    };
};
/**
 * CZNA-395 — creates a new vocabulary type (registry row) and invalidates
 * every cached `useVocabularyTypes` variant (all validationType/includeBindings
 * combinations share the `czna-vocabulary-types` key prefix) so the picker
 * that triggered the create sees the new type on its next render. Callers
 * MUST supply `onError` (project rule).
 */
export var useCreateVocabularyType = function (options) {
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    return useMutation(function (data) { return createVocabularyType(ky, data); }, __assign(__assign({}, options), { onSuccess: function (data, vars, ctx) {
            var _a;
            queryClient.invalidateQueries([VOCABULARY_TYPES_KEY]);
            (_a = options === null || options === void 0 ? void 0 : options.onSuccess) === null || _a === void 0 ? void 0 : _a.call(options, data, vars, ctx);
        } }));
};
/**
 * CZNA-1470 — renames a vocabulary type and invalidates every cached registry
 * variant, like {@link useCreateVocabularyType}. Callers MUST supply `onError`.
 */
export var useUpdateVocabularyType = function (options) {
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    return useMutation(function (_a) {
        var id = _a.id, body = _a.body;
        return updateVocabularyType(ky, id, body);
    }, __assign(__assign({}, options), { onSuccess: function (data, vars, ctx) {
            var _a;
            queryClient.invalidateQueries([VOCABULARY_TYPES_KEY]);
            (_a = options === null || options === void 0 ? void 0 : options.onSuccess) === null || _a === void 0 ? void 0 : _a.call(options, data, vars, ctx);
        } }));
};
export default useVocabularyTypes;
